/**
 * Display and payload rules for Newsroom, the news review lane.
 *
 * This module exists separately from the pages for two reasons.
 *
 * First, the settings payload builder. The tab this replaced shipped five
 * safety toggles that were never wired to anything — users flipped them, the
 * switch animated, and no request was ever sent. The specific trap for a
 * toggle is that `enabled: false` is falsy, so any payload built by truthiness
 * checks silently drops the "off" case and a site can never actually be turned
 * off. buildSettingsPayload always emits every field, and the tests assert the
 * `false` case directly.
 *
 * Second, the normalisers. `gate_results` and `quote_manifest` are the two
 * places the API contract did not pin a shape down. Rather than guess one and
 * crash on anything else, they accept every plausible encoding and drop what
 * they cannot read. A gate the backend adds next month renders as a humanised
 * label instead of vanishing — the same rule the site activity log follows,
 * and the right default when the thing being rendered is safety evidence.
 */

import type {
  NewsroomGate,
  NewsroomQuote,
  NewsroomRejection,
  NewsroomSettings,
  NewsroomSettingsPayload,
  NewsroomStatus,
  NewsroomTier,
  RejectionGroup,
} from "@/types/newsroom.types"

export const NEWSROOM_PER_PAGE = 25

/** The queue lanes, in the order they are offered. Review comes first because it is the job. */
export const NEWSROOM_STATUSES: { value: NewsroomStatus; label: string }[] = [
  { value: "needs_review", label: "Needs Review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "ingested", label: "Ingested" },
]

const STATUS_LABELS: Record<string, string> = {
  needs_review: "Needs Review",
  approved: "Approved",
  rejected: "Rejected",
  ingested: "Ingested",
}

/** A readable status label, humanising any lane added after this build shipped. */
export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? humanise(status)
}

/**
 * Per-tier presentation. Severity runs a > b > c.
 *
 * - a: deaths, victims, minors, arrests, named private individuals. These are
 *   auto-rejected at INGEST, before any model spend, which means a tier A
 *   item should never appear in any queue at all. If one does, the ingest
 *   gate has a bug — so it is rendered as an anomaly to report, not as a
 *   routine caution. This is the exact class of content the incident was
 *   about; seeing one here is a signal about the system, not the story.
 * - b: caution. An institutional story touching sensitive ground —
 *   litigation, investigations, allegations, recalls. It may be drafted and
 *   published, but never quietly. This is the tier the warning badge is for.
 * - c: routine. Ordinary institutional news, the normal case, no badge.
 *
 * `warning` being non-null is what makes a row show a badge; `anomaly` is
 * what escalates it from a caution to an alert.
 */
export const TIER_META: Record<
  NewsroomTier,
  { label: string; warning: string | null; anomaly: boolean }
> = {
  a: { label: "Tier A", warning: "Should not be here — report this", anomaly: true },
  b: { label: "Tier B", warning: "Sensitive — extra care", anomaly: false },
  c: { label: "Tier C", warning: null, anomaly: false },
}

export function tierMeta(tier: string | null | undefined): {
  label: string
  warning: string | null
  anomaly: boolean
} {
  const known = tier == null ? undefined : TIER_META[tier as NewsroomTier]
  if (known) return known

  // A MISSING tier is a real API value, not a hypothetical. Gate 0 assigns the
  // tier at its third check, so anything rejected by the two before it —
  // channel policy, source policy — is stored with tier null and appears in
  // the Rejected lane that way. This branch used to call .toUpperCase() on it
  // and take the whole lane down with a TypeError.
  if (tier == null || tier === "") {
    return {
      label: "Not tiered",
      // Fail safe, and honest about why: it never reached the tiering step.
      warning: "Rejected before tiering",
      anomaly: false,
    }
  }

  return {
    label: `Tier ${tier.toUpperCase()}`,
    // Fail safe: an unrecognised tier renders as sensitive rather than
    // routine. It is not treated as an ingest bug, because an unknown value is
    // far more likely to be a tier added after this build.
    warning: "Unrecognised tier — treated as sensitive",
    anomaly: false,
  }
}

/** True when this tier should be visibly flagged to the reviewer. */
export function isSensitiveTier(tier: string | null | undefined): boolean {
  return tierMeta(tier).warning !== null
}

/**
 * True when this item's presence in a queue is itself the problem. Tier A is
 * rejected at ingest, so one reaching a reviewer means the ingest gate let it
 * through.
 */
export function isTierAnomaly(tier: string): boolean {
  return tierMeta(tier).anomaly
}

/** Query params for GET /newsroom/queue. */
export function buildQueueParams(
  status: NewsroomStatus,
  page = 1,
  perPage: number = NEWSROOM_PER_PAGE,
): Record<string, string | number> {
  return {
    status,
    page: Math.max(1, Math.trunc(page)),
    per_page: Math.min(Math.max(1, Math.trunc(perPage)), 100),
  }
}

/**
 * The settings body for PUT /newsroom/settings.
 *
 * Every field is always present — see the module note on why an omitted
 * `enabled: false` is the exact bug that made the old toggles inert. Strings
 * are trimmed because a byline of " " renders as an empty attribution.
 * `vertical` is admin-set and deliberately not writable here.
 */
/**
 * Unwrap the settings envelope. GET and PUT both answer with
 * `{ settings: {...} }`, never the bare object.
 *
 * This lives here rather than inline in api.ts so it is reachable from a test.
 * Getting it wrong was not a typing nitpick: the tab spreads its result over
 * its defaults, so an unwrapped envelope left every real field unread — the
 * vertical looked unassigned, the webhook URL never rendered, and pressing
 * Save wrote the empty defaults back over the client's beat and byline.
 */
export function readSettingsResponse(body: unknown): NewsroomSettings {
  if (body && typeof body === "object" && "settings" in body) {
    const inner = (body as { settings: unknown }).settings
    if (inner && typeof inner === "object") return inner as NewsroomSettings
  }

  // Tolerated rather than trusted: a body that is already flat still renders,
  // so an API that stops wrapping does not blank the page.
  return (body ?? {}) as NewsroomSettings
}

export function buildSettingsPayload(settings: NewsroomSettings): NewsroomSettingsPayload {
  return {
    enabled: settings.enabled === true,
    beat: (settings.beat ?? "").trim(),
    byline_name: (settings.byline_name ?? "").trim(),
    byline_url: (settings.byline_url ?? "").trim(),
  }
}

/**
 * Newsroom cannot be enabled without a valid vertical — PUT returns 422.
 * Checking up front means the toggle explains itself instead of failing.
 */
export function hasValidVertical(settings: NewsroomSettings | undefined | null): boolean {
  return settings?.vertical?.is_valid === true
}

export const NO_VERTICAL_MESSAGE =
  "This site has no vertical assigned, so Newsroom cannot be switched on. An admin sets the vertical — it decides which stories the site may publish at all."

/**
 * A save failure worth reading. The 422 on enabling without a vertical is the
 * likeliest error here and it is actionable, so it gets its own message
 * rather than a generic failure toast.
 */
export function saveErrorMessage(
  status: number | undefined,
  wasEnabling: boolean,
  fallback: string,
): string {
  if (status === 422 && wasEnabling) return NO_VERTICAL_MESSAGE
  return fallback
}

/**
 * Feed ingestion for this site. A null webhook_url means the vertical does
 * not permit the rss.app channel — legal_personal_injury denies it outright —
 * which is an answer to render, not a missing value or an error.
 */
export function feedIngestion(settings: NewsroomSettings | undefined | null): {
  permitted: boolean
  url: string | null
} {
  const url = settings?.webhook_url ?? null
  return { permitted: url !== null, url }
}

/** True when the form differs from what the server last returned. */
export function isSettingsDirty(saved: NewsroomSettings, draft: NewsroomSettings): boolean {
  const a = buildSettingsPayload(saved)
  const b = buildSettingsPayload(draft)
  return (
    a.enabled !== b.enabled ||
    a.beat !== b.beat ||
    a.byline_name !== b.byline_name ||
    a.byline_url !== b.byline_url
  )
}

/**
 * Curated labels for the gates the pipeline runs. The four confirmed keys are
 * first; the rest are spelling variants kept so a rename does not blank a row
 * of the safety checklist. Unknown keys are humanised, because gates are
 * still being added and one that renders is worth more than one that vanishes.
 */
const GATE_LABELS: Record<string, string> = {
  pii_scan: "PII scan",
  link_slug_sanitizer: "Link slug sanitiser",
  source_overlap: "Source overlap",
  centroid_dedup: "Duplicate event check",

  pii: "PII scan",
  pii_check: "PII scan",
  quote_verification: "Quote verification",
  quotes: "Quote verification",
  quote_check: "Quote verification",
  plagiarism: "Plagiarism check",
  plagiarism_check: "Plagiarism check",
  overlap: "Source overlap",
  ngram_overlap: "Source overlap",
  similarity: "Similarity to source",
  primary_source: "Primary source linked",
  primary_sources: "Primary source linked",
  source_count: "Independent sources",
  dedup: "Duplicate event check",
  event_dedup: "Duplicate event check",
  recency: "Recency floor",
  vertical_policy: "Vertical policy",
  tier_policy: "Sensitivity tier policy",
}

/** A readable gate label, humanising gates this build has never heard of. */
export function gateLabel(key: string): string {
  return GATE_LABELS[key] ?? humanise(key)
}

/**
 * Read `gate_results`.
 *
 * The API sends an object keyed by gate name whose values are always objects
 * with `passed`, `detail` and `skipped`. The extra shapes accepted here —
 * bare booleans, arrays of records, status strings — cost nothing and mean a
 * change of encoding degrades instead of blanking the safety checklist.
 *
 * Anything uninterpretable is dropped rather than rendered as a false
 * "passed": a gate we cannot read must never look like one that was verified.
 */
export function normaliseGateResults(raw: unknown): NewsroomGate[] {
  if (!raw || typeof raw !== "object") return []

  const entries: [string, unknown][] = Array.isArray(raw)
    ? raw.map((entry) => [readGateKey(entry), entry])
    : Object.entries(raw as Record<string, unknown>)

  const gates: NewsroomGate[] = []

  for (const [key, value] of entries) {
    if (!key) continue

    const passed = readGatePassed(value)
    if (passed === null) continue

    gates.push({
      key,
      label: gateLabel(key),
      passed,
      skipped: readGateSkipped(value),
      detail: readGateDetail(value),
    })
  }

  return gates
}

/** A skipped gate did not run, whatever `passed` happens to say. */
function readGateSkipped(value: unknown): boolean {
  if (!value || typeof value !== "object") return false
  return (value as Record<string, unknown>).skipped === true
}

function readGateKey(entry: unknown): string {
  if (!entry || typeof entry !== "object") return ""
  const record = entry as Record<string, unknown>
  for (const field of ["key", "name", "gate", "id"]) {
    const value = record[field]
    if (typeof value === "string" && value.trim()) return value.trim()
  }
  return ""
}

/** null means "could not tell", which is not the same as "failed". */
function readGatePassed(value: unknown): boolean | null {
  if (typeof value === "boolean") return value
  if (!value || typeof value !== "object") return null

  const record = value as Record<string, unknown>
  for (const field of ["passed", "pass", "ok", "success", "verified", "result"]) {
    const candidate = record[field]
    if (typeof candidate === "boolean") return candidate
  }

  // A bare status string is common enough to be worth reading.
  const status = record.status
  if (typeof status === "string") {
    const normalised = status.trim().toLowerCase()
    if (["pass", "passed", "ok", "clear", "clean"].includes(normalised)) return true
    if (["fail", "failed", "flag", "flagged", "blocked"].includes(normalised)) return false
  }

  return null
}

function readGateDetail(value: unknown): string | null {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  for (const field of ["detail", "details", "message", "note", "summary"]) {
    const candidate = record[field]
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim()
  }
  return null
}

/**
 * Counts for the "what the machine verified" header.
 *
 * Skipped gates are counted apart from passes, and `allPassed` requires that
 * there are none. A run where half the checks never executed is not a clean
 * run, and reporting it as one is the failure mode this system exists to
 * avoid repeating.
 */
export function gateSummary(gates: NewsroomGate[]): {
  passed: number
  total: number
  failed: NewsroomGate[]
  skipped: NewsroomGate[]
  allPassed: boolean
} {
  const skipped = gates.filter((gate) => gate.skipped)
  const failed = gates.filter((gate) => !gate.skipped && !gate.passed)
  const passed = gates.filter((gate) => !gate.skipped && gate.passed).length

  return {
    passed,
    total: gates.length,
    failed,
    skipped,
    // No gates at all is not "all passed" — there is nothing to have passed.
    allPassed: gates.length > 0 && failed.length === 0 && skipped.length === 0,
  }
}

/**
 * Read `quote_manifest` if it is an array of quote records. Fabricated quotes
 * are a documented failure mode for this kind of pipeline, so an unreadable
 * manifest shows nothing rather than implying the quotes were checked.
 */
export function normaliseQuoteManifest(raw: unknown): NewsroomQuote[] {
  if (!Array.isArray(raw)) return []

  const quotes: NewsroomQuote[] = []

  for (const entry of raw) {
    if (typeof entry === "string") {
      if (entry.trim()) {
        quotes.push({
          quote: entry.trim(),
          speaker: null,
          sourceUrl: null,
          sourceTitle: null,
          verified: null,
        })
      }
      continue
    }
    if (!entry || typeof entry !== "object") continue

    const record = entry as Record<string, unknown>
    const text = firstString(record, ["quote", "text", "content", "value"])
    if (!text) continue

    quotes.push({
      quote: text,
      speaker: firstString(record, ["speaker", "attributed_to", "author"]),
      sourceUrl: firstString(record, ["source_url", "url", "sourceUrl", "source"]),
      sourceTitle: firstString(record, ["source_title", "sourceTitle"]),
      verified: readGatePassed(record),
    })
  }

  return quotes
}

function firstString(record: Record<string, unknown>, fields: string[]): string | null {
  for (const field of fields) {
    const value = record[field]
    if (typeof value === "string" && value.trim()) return value.trim()
  }
  return null
}

/**
 * Queue-row flags. The vocabulary is closed today but append-only, so unknown
 * values are humanised rather than dropped — a flag added next month should
 * warn, not vanish.
 */
const FLAG_LABELS: Record<string, string> = {
  tier_b_caution: "Tier B caution",
  no_primary_source_link: "No primary source link",
  primary_source_link_names_a_party: "Published link contains a party's name",
}

export function flagLabel(flag: string): string {
  return FLAG_LABELS[flag] ?? humanise(flag)
}

/**
 * The one flag that means a name ships visible in published output.
 *
 * Court and agency URLs carry case captions, so a name-shaped slug on a
 * primary-source domain is kept rather than rewritten — that link is both the
 * citation the primary-source gate requires and the fair-report privilege
 * that makes covering a proceeding defensible. Rewriting it to a homepage
 * would break the one gate nothing else can satisfy.
 *
 * Everywhere else a name in an href is rewritten silently. Here the system is
 * knowingly publishing one and asking a human to agree, so it gets its own
 * callout and its own line on the approve confirmation rather than sitting in
 * a row of chips.
 */
export const NAME_IN_LINK_FLAG = "primary_source_link_names_a_party"

export function namesAPartyInLink(flags: string[] | undefined): boolean {
  return (flags ?? []).includes(NAME_IN_LINK_FLAG)
}

/**
 * Flags worth showing beside a tier badge that already says the same thing.
 * `tier_b_caution` is exactly the badge's own message, so repeating it as a
 * chip adds noise to the row without adding information.
 */
export function visibleFlags(flags: string[] | undefined, tier: string | null | undefined): string[] {
  if (!flags) return []
  return flags.filter((flag) => !(flag === "tier_b_caution" && tier === "b"))
}

/**
 * Rejection groups, which do not mean the same thing to a reader of the
 * Rejected lane.
 *
 * `safety` is the system working correctly and should read as such — a wall
 * of safety rejections is a healthy newsroom. `infrastructure` means Forge
 * broke rather than the story being bad, and those items are re-runnable, so
 * filing them next to safety rejections would hide real work behind a wall of
 * things that look handled.
 */
export const REJECTION_GROUPS = [
  "safety",
  "editorial",
  "duplicate",
  "quality",
  "infrastructure",
] as const

/**
 * The group for a rejection, taken from the API and never inferred.
 *
 * The codes are flat strings with no family prefix — `pii_in_draft` is
 * safety, `blocked_403` is infrastructure — so parsing them would fail
 * silently on every real value. Anything unrecognised is "unknown", which
 * renders neutrally: a mislabelled safety rejection is worse than an
 * unlabelled one.
 */
export function rejectionGroup(rejection: NewsroomRejection | null | undefined): RejectionGroup {
  const group = rejection?.group?.trim().toLowerCase()
  if (group && (REJECTION_GROUPS as readonly string[]).includes(group)) {
    return group as RejectionGroup
  }
  return "unknown"
}

/**
 * True when the rejection was Forge's fault rather than a judgement on the
 * story, which means the item can be run again once the fault is fixed.
 */
export function isReRunnable(rejection: NewsroomRejection | null | undefined): boolean {
  return rejectionGroup(rejection) === "infrastructure"
}

/**
 * Display text for a rejection. `label` is pre-humanised by the API, so it is
 * rendered as sent; the code is only a fallback for a payload missing one.
 */
export function rejectionText(rejection: NewsroomRejection | null | undefined): string {
  const label = rejection?.label?.trim()
  if (label) return label

  const code = rejection?.code?.trim()
  return code ? humanise(code) : ""
}

/**
 * Quick-pick rejection reasons, drawn from the categories that are hard
 * auto-rejects for this content class. They fill the reason box rather than
 * submitting, so the reviewer can always add specifics.
 */
export const REJECTION_REASONS: string[] = [
  "Names a private individual",
  "Death, injury or crime victim",
  "Involves a minor",
  "Single source / unverified",
  "Active investigation or litigation",
  "Quote could not be verified",
  "Too close to the source text",
  "Off-beat for this site",
]

/** A reason is required, and whitespace is not a reason. */
export function isValidRejectionReason(reason: string): boolean {
  return reason.trim().length > 0
}

/**
 * The approve confirmation copy.
 *
 * Approving publishes to a live client site with no staging step, so the
 * wording names the destination domain and the byline the piece will carry.
 * It lives here so the tests pin the exact sentences — this is the last thing
 * a reviewer reads before a story goes public.
 */
export function approveConfirmation(
  domain: string,
  bylineName: string,
): { title: string; body: string; byline: string; confirmLabel: string } {
  const site = domain.trim() || "the client site"
  const byline = bylineName.trim()

  return {
    title: "Approve this story?",
    body: `This does NOT publish. The article is saved to Content as a draft, where it can be edited and, separately, published to ${site}. Approving says the story is worth running.`,
    byline: byline
      ? `It will carry the byline ${byline} when it is published.`
      : "No byline is configured. Set one before publishing, or the article goes out with no author attribution.",
    confirmLabel: "Approve to drafts",
  }
}

/**
 * Compact age for a queue row: "just now", "12m", "5h", "3d".
 * `now` is injectable so the tests are not clock-dependent.
 */
export function formatAge(iso: string | null, now: Date = new Date()): string {
  if (!iso) return "—"

  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return "—"

  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000)
  // A timestamp in the future is a clock skew, not an age.
  if (seconds < 60) return "just now"

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`

  return `${Math.floor(hours / 24)}d`
}

/**
 * Terms that read wrong in title case. This path only ever renders keys the
 * build has not seen before, so "PII In Draft" beating "Pii In Draft" is the
 * difference between a new gate looking deliberate and looking broken.
 */
const ACRONYMS = new Set(["pii", "url", "urls", "api", "id", "ids", "seo", "ai", "html", "js", "ymyl"])

/** "primary_source_present" -> "Primary Source Present". */
function humanise(value: string): string {
  const text = value
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\S+/g, (word) =>
      ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )

  return text || value
}
